import { NubankService } from "@libs/renegkit";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { baseFilters, partnersFilters } from "../filters";
import type { DebtFilter, FormattedDebt } from "../types";

const debtFilterParam = "debts_filter";

interface DebtsFilteringProps {
	fullDebtList: FormattedDebt[];
	isStatusPartial: boolean;
	nubankExperience?: NubankService.NubankExperienceType;
}

export default function useDebtsFiltering({
	fullDebtList,
	isStatusPartial,
	nubankExperience,
}: DebtsFilteringProps) {
	const [filteredDebts, setFilteredDebts] = useState(fullDebtList);
	const filteredDebtsCount = filteredDebts.length;
	const [filterList, setFilterList] = useState<DebtFilter[]>([]);
	const [hasFilter, setHasFilter] = useState(false);
	const [selectedFilter, setSelectedFilter] = useState<DebtFilter | null>(null);
	const [searchParams, setSearchParams] = useSearchParams();
	const [notFoundDebtsText, setNotFoundDebtsText] = useState<string>();
	const isNubankLPExp = nubankExperience === NubankService.NUBANK_CAMPAIGN_LP;

	useEffect(loadFilterFromUrl, []);
	useEffect(applyDebtFilters, [selectedFilter, fullDebtList]);

	function loadFilterFromUrl() {
		const urlFilter = isNubankLPExp
			? "nubank"
			: searchParams.get(debtFilterParam) || "";
		const allFilters = [...baseFilters, ...partnersFilters];
		const selectedFilter = allFilters.find((filter) => filter.id === urlFilter);
		if (selectedFilter) {
			const isPartnerFilter = partnersFilters.includes(selectedFilter);
			const displayedFilters = isPartnerFilter
				? [...baseFilters, selectedFilter]
				: baseFilters;
			setFilterList(displayedFilters);
			setHasFilter(true);
			setSelectedFilter(selectedFilter);
		}
	}

	function applyDebtFilters() {
		if (!selectedFilter) return;

		const filteredDebts = selectedFilter.filterFn(fullDebtList);
		const shouldApplyFilter = filteredDebts.length || isNubankLPExp;

		if (shouldApplyFilter) {
			setFilteredDebts(filteredDebts);
			setNotFoundDebtsText("");
		} else if (!isStatusPartial) {
			const notFoundFilteredDebtsTxt = `Infelizmente <strong>não encontramos dívidas na categoria “${selectedFilter.label}”</strong>, mas você tem outras dívidas com propostas imperdíveis.`;
			setNotFoundDebtsText(notFoundFilteredDebtsTxt);
			clearFilter();
		}
	}

	function selectFilter(filter: DebtFilter) {
		setSelectedFilter(filter);
		searchParams.set(debtFilterParam, filter.id);
		setSearchParams(searchParams);
	}

	function clearFilter() {
		if (selectedFilter) {
			searchParams.delete(debtFilterParam);
			setSearchParams(searchParams);
			setSelectedFilter(null);
		}
	}

	function clearNotFoundDebtsMsg() {
		setNotFoundDebtsText(undefined);
	}

	return {
		filteredDebts,
		filterList,
		clearFilter,
		selectFilter,
		selectedFilter,
		hasFilter,
		notFoundDebtsText,
		clearNotFoundDebtsMsg,
		filteredDebtsCount,
	};
}
